import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Single-line, ellipsised value cell for the Type cards (Storefront, Window).
// Clipping is measured from the rendered box (scrollWidth vs clientWidth) and
// re-measured on resize, so the full-value tooltip is offered only when the
// text is actually cut off.
export const TruncatedValue = ({ value, className = '', ...rest }) => {
  const ref = useRef(null);
  const [clipped, setClipped] = useState(false);
  const [tip, setTip] = useState(null);
  const text = (value === null || value === undefined || value === '') ? 'N/A' : String(value);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setClipped(el.scrollWidth > el.clientWidth + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el || el.scrollWidth <= el.clientWidth + 1) return;
    const r = el.getBoundingClientRect();
    setTip({ left: r.left, top: r.bottom + 6 });
  }, []);

  const hide = useCallback(() => setTip(null), []);

  return (
    <>
      <div
        ref={ref}
        className={`truncate ${className}`}
        tabIndex={clipped ? 0 : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        {...rest}
      >
        {text}
      </div>
      {tip && createPortal(
        <div
          role="tooltip"
          data-testid="truncated-value-tooltip"
          style={{ position: 'fixed', left: tip.left, top: tip.top, maxWidth: 320 }}
          className="z-[100] pointer-events-none rounded-md bg-slate-900 px-3 py-1.5 text-xs font-normal text-white shadow-lg break-words"
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
};

export default TruncatedValue;
